import {
  DatasetFilterPayload,
  GridConfigAggregationType,
  H3Configuration
} from "./dataset";

export enum WidgetType {
  BAR_V = "BAR_VERTICAL",
  BAR_H = "BAR_HORIZONTAL",
  DONUT = "DONUT",
  LINE = "LINE",
  BIG_NUMBER = "BIG_NUMBER"
}

export enum OrderByType {
  ASC = "ASC",
  DESC = "DESC"
}

export enum AggregationType {
  COUNT = "COUNT",
  SUM = "SUM",
  AVG = "AVG",
  MIN = "MIN",
  MAX = "MAX",
  MEDIAN = "MEDIAN",
  COUNT_DISTINCT = "COUNT_DISTINCT"
}

export interface PostWidgetAnalysisConfigPayload {
  id: string;
  dimension?: string;
  measure?: string;
  aggregation?: AggregationType;
  order_by?: OrderByType;
  filters?: DatasetFilterPayload[];
  grid_config?: {
    level?: number;
    aggregation_type?: GridConfigAggregationType;
  };
  group_by?: string;
}

export const WidgetDefaultType = {
  SYSTEM_GENERATED: true,
  USER_CREATED: false
} as const;

export type WidgetDefaultType =
  (typeof WidgetDefaultType)[keyof typeof WidgetDefaultType];

export interface WidgetDefaultConfiguration {
  /**  Persisted on save (shaped `${datasetId}_${instance}_${n}`) so each widget
   can be re-linked to a specific dataset instance on load. Optional because
  configs saved before the widget refactor don't carry it.
  */
  widget_id?: string;
  is_default: WidgetDefaultType;
  dataset_id: string;
  label: string;
  chart_type: WidgetType;
  dimension: string;
  measure: string;
  aggregation: AggregationType; // COUNT, SUM, AVG, MIN, MAX, MEDIAN
  configuration: {
    dimension_values_shown: number; // line_shown, bar_shown, slice_shown
    order_by: OrderByType;
  };
  h3Configuration?: H3Configuration | null;
  group_by?: string;
}

export interface PostWidgetDefaultConfigPayload {
  id: string;
  grid_config?: { aggregation_type: GridConfigAggregationType };
  group_by?: string;
}

export interface PostWidgetDefaultConfigResponse {
  // BE now returns multiple default widgets per dataset/analysis (ORB-3440,
  // enabled by ORB-3372) instead of a single object.
  data: WidgetDefaultConfiguration[];
}

export interface WidgetConfig extends WidgetDefaultConfiguration {
  widget_id: string; // with extra suffix `_n`
}

export type WidgetConfigWithoutWidgetId = Omit<WidgetConfig, "widget_id">;

export interface WidgetMap {
  [id: string]: WidgetConfig;
}
export interface AnalysisDataPoint {
  label: string;
  value: number;
}

export interface WidgetAnalysisConfigResult {
  aggregation?: string;
  dimension: string;
  measure: string;
  data?: AnalysisDataPoint[];
  rows?: Record<string, string | number>[];
  value?: number;
}

export interface PostWidgetAnalysisResponse {
  data: WidgetAnalysisConfigResult;
}

export type WidgetPreviewData =
  | WidgetAnalysisConfigResult["data"]
  | WidgetAnalysisConfigResult["rows"]
  | { type: AggregationType; value: number; measure: string }[];

export interface PostAnalysisWidgetAnalyticsPayload {
  analysisId: string;
  datasetId: string;
  dimension?: string;
  measure?: string;
  aggregation?: AggregationType;
  order_by?: OrderByType;
  filters?: DatasetFilterPayload[];
  grid_config?: {
    level?: number;
    aggregation_type?: GridConfigAggregationType;
  };
  group_by?: string;
}

export interface PostAnalysisWidgetDefaultConfigPayload {
  analysisId: string;
  datasetId: string;
  grid_config?: { aggregation_type: GridConfigAggregationType };
  group_by?: string;
}

export interface PutAnalysisWidgetConfigItem {
  dataset_id: string;
  label: string;
  chart_type: WidgetType;
  dimension: string;
  measure: string;
  aggregation: AggregationType;
  configuration: {
    dimension_values_shown: number;
    order_by: OrderByType;
  };
  group_by?: string;
}

export interface PutAnalysisWidgetConfigPayload {
  filter_by_catchment_ids: string[];
  widgets: PutAnalysisWidgetConfigItem[];
}

export interface GetAnalysisWidgetConfigItem {
  dataset_id: string;
  label: string;
  chart_type: WidgetType;
  dimension: string;
  measure: string;
  aggregation: AggregationType;
  configuration: {
    dimension_values_shown: number;
    order_by: OrderByType;
  };
  grid_config?: {
    aggregation_type: GridConfigAggregationType;
    level: number;
  };
  group_by_attribute_id?: string;
}

export interface GetAnalysisWidgetConfigResponse {
  data: {
    filter_by_catchment_ids: string[];
    widgets: GetAnalysisWidgetConfigItem[];
  } | null;
}
